import React, { ErrorInfo, ReactNode } from 'react';
import * as Sentry from "@sentry/react";
import { logger } from '../utils/logger';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class GlobalErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error, errorInfo: null };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Usar logger en lugar de console.error
        logger.error('GlobalErrorBoundary caught an error', error, {
            componentStack: errorInfo.componentStack,
            errorBoundary: 'global'
        });

        // Integrar con Sentry
        Sentry.withScope((scope) => {
            scope.setExtras({
                componentStack: errorInfo.componentStack,
                errorBoundary: 'global'
            });
            Sentry.captureException(error);
        });
        
        // V2148: Handle ChunkLoadError or Mime Type errors from old assets
        const errorMessage = error.message?.toLowerCase() || '';
        const isChunkError = 
            errorMessage.includes('loading chunk') || 
            errorMessage.includes('mime type') || 
            errorMessage.includes('not a valid javascript mime type') ||
            errorMessage.includes('failed to fetch') ||
            errorMessage.includes('dynamically imported module');

        if (isChunkError) {
            console.warn("CRITICAL: Detected obsolete chunk or assets. Forcing hard reload to clear cache...");
            // Use a shorter delay for faster recovery
            setTimeout(() => {
                window.location.reload();
            }, 100);
        }

        this.setState({ hasError: true, error, errorInfo });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="p-4 bg-white dark:bg-dark-bg text-slate-900 dark:text-white h-screen overflow-auto flex flex-col items-center justify-center font-sans">
                    <div className="max-w-md w-full bg-white dark:bg-dark-card p-8 rounded-[2rem] shadow-2xl border border-red-500/10 text-center">
                        <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 14c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-black mb-4">Algo salió mal (Crash)</h1>
                        <p className="mb-8 text-slate-500 dark:text-slate-400">La aplicación ha encontrado un error crítico. Por favor, intenta recargarla.</p>
                        <button
                            className="w-full py-4 bg-red-600 text-white font-bold rounded-2xl shadow-lg hover:bg-red-700 active:scale-95 transition-all"
                            onClick={() => window.location.reload()}
                        >
                            Recargar Aplicación
                        </button>
                    </div>
                    {/* Solo mostrar stack trace en desarrollo */}
                    {process.env.NODE_ENV === 'development' && this.state.error && (
                        <div className="mt-8 w-full max-w-2xl bg-red-100 dark:bg-red-900/20 p-4 rounded-lg overflow-x-auto text-xs">
                            <p className="font-bold mb-2 text-red-800 dark:text-red-200">{this.state.error?.toString()}</p>
                            <pre className="whitespace-pre-wrap text-red-700 dark:text-red-300">{this.state.errorInfo?.componentStack}</pre>
                        </div>
                    )}
                </div>
            );
        }

        return this.props.children;
    }
}

export default GlobalErrorBoundary;
